import React from 'react';
import {
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  Typography,
  Box,
  Checkbox,
  Tooltip,
} from '@mui/material';
import PropTypes from 'prop-types';

// For accessibility - to hide content visually but keep it available for screen readers
const visuallyHiddenStyle = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: -1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
};

const headerLabelSx = {
  display: 'block',
  maxWidth: 160,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontWeight: 600,
  color: 'rgba(0, 0, 0, 0.87)',
  fontSize: '14px',
};

export default function TableHeaderRow(props) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
    columns,
    actionsColumn = true,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead sx={{ backgroundColor: '#F5F7FA' }}>
      <TableRow>
        <TableCell
          padding="checkbox"
          sx={{
            py: 1.5,
            backgroundColor: '#F5F7FA',
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          }}
        >
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              'aria-label': 'select all submissions',
            }}
            size="small"
          />
        </TableCell>
        {columns?.map((headCell) => {
          if (!headCell.isVisible) return null;
          return (
            <TableCell
              key={headCell.id}
              align={headCell.numeric ? 'right' : 'left'}
              padding={headCell.disablePadding ? 'none' : 'normal'}
              sortDirection={orderBy === headCell.id ? order : false}
              sx={{
                py: 1.5,
                backgroundColor: '#F5F7FA',
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                color: 'rgba(0, 0, 0, 0.87)',
              }}
            >
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                <Tooltip title={headCell.label} placement="top" arrow>
                  <Typography sx={headerLabelSx}>{headCell.label}</Typography>
                </Tooltip>
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHiddenStyle}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
          );
        })}
        {/* Actions column */}
        {actionsColumn && (
          <TableCell
            key="actions"
            align="left"
            padding="normal"
            sortDirection={orderBy === 'actions' ? order : false}
            sx={{
              py: 1.5,
              backgroundColor: '#F5F7FA',
              borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
              color: 'rgba(0, 0, 0, 0.87)',
            }}
          >
            <Tooltip title="Actions" placement="top" arrow>
              <Typography sx={headerLabelSx}>Actions</Typography>
            </Tooltip>
          </TableCell>
        )}
      </TableRow>
    </TableHead>
  );
}

TableHeaderRow.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
  columns: PropTypes.array.isRequired,
  actionsColumn: PropTypes.bool,
};

TableHeaderRow.defaultProps = {
  actionsColumn: true,
};
